import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router, Routes, Route,
  Navigate, NavLink
} from 'react-router-dom';
import './App.css';
import LoginPage  from './Account/LoginPage';
import SignUp     from './Account/SignUp';
import Home       from './Home/Home';
import Rides      from './Rides/Rides';
import Activity   from './Activity/Activity';
import Profile    from './Profile/Profile';
import Settings   from './Settings/Settings';
import Admin from './Admin/Admin';
import PostForm   from './Post/PostForm';
import Borrow     from './Borrow/Borrow';
import { getAuth, onAuthStateChanged, sendEmailVerification } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';
import './firebase-config';
import { syncOwnContact } from './contacts';
import { useNeedsTerms } from './safety';
import { TermsAgreement } from './Safety/Safety';

/* ── SVG icon set ─────────────────────────────────────────── */
const IC = {
  home: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z"/>
    </svg>
  ),
  book: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13"/>
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
      <circle cx="5.5" cy="18.5" r="2.5"/>
      <circle cx="18.5" cy="18.5" r="2.5"/>
    </svg>
  ),
  request: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  borrow: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="8" y1="12" x2="16" y2="12"/>
      <line x1="12" y1="8" x2="12" y2="16"/>
    </svg>
  ),
  activity: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  profile: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
};

/* ── Top Navbar ───────────────────────────────────────────── */
function TopNav({ initials }) {
  return (
    <nav className="topnav">
      <div className="topnav-logo">
        <img src={process.env.PUBLIC_URL + '/favicon-rounded.png'} alt="FalconRide" className="topnav-logo-img" />
        <span className="topnav-logo-text">FalconRide</span>
      </div>
      <div className="topnav-divider" />
      <div className="topnav-links">
        <NavLink to="/home"     className={({ isActive }) => isActive ? 'active' : ''}>Home</NavLink>
        <NavLink to="/rides"    className={({ isActive }) => isActive ? 'active' : ''}>Rides</NavLink>
        <NavLink to="/borrow"   className={({ isActive }) => isActive ? 'active' : ''}>Borrow a Car</NavLink>
        <NavLink to="/activity" className={({ isActive }) => isActive ? 'active' : ''}>Activity</NavLink>
      </div>
      <div className="topnav-right">
        <NavLink to="/profile" className="topnav-avatar">
          {initials}
        </NavLink>
      </div>
    </nav>
  );
}

/* ── Verify Email Banner ──────────────────────────────────── */
function VerifyEmailBanner() {
  const [sent,      setSent]      = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const auth = getAuth();

  const resend = async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
      setSent(true);
    }
  };

  if (dismissed) return null;

  return (
    <div style={{
      background: '#fff8e1', borderBottom: '1px solid #ffe082',
      padding: '10px 20px', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
      fontSize: '14px', color: '#5d4037',
    }}>
      <span>
        ✉️ Please verify your email address to keep your account secure.{' '}
        {sent
          ? <strong>Verification email sent!</strong>
          : <button onClick={resend} style={{ background: 'none', border: 'none', color: '#1565c0', cursor: 'pointer', fontWeight: 600, padding: 0 }}>Resend email</button>
        }
      </span>
      <button onClick={() => setDismissed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '16px' }}>✕</button>
    </div>
  );
}

/* ── App ──────────────────────────────────────────────────── */
function App() {
  const [isLoggedIn,    setIsLoggedIn]    = useState(false);
  // Firebase restores a saved sign-in asynchronously; until it has, we don't
  // know which screen to show (otherwise the login page flashes first).
  const [authReady,     setAuthReady]     = useState(false);
  const [isVerified,    setIsVerified]    = useState(true);
  const [initials,      setInitials]      = useState('?');
  const needsTerms = useNeedsTerms();
  const auth = getAuth();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setIsLoggedIn(!!user);
      setAuthReady(true);
      setIsVerified(user?.emailVerified ?? true);
      if (user) {
        syncOwnContact(); // keep the private contact entry complete
        try {
          const snap = await get(ref(getDatabase(), `users/${user.uid}`));
          const name = snap.val()?.name || '';
          const parts = name.trim().split(' ');
          setInitials(
            parts.length >= 2
              ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
              : name.slice(0, 2).toUpperCase() || user.email?.charAt(0).toUpperCase() || '?'
          );
        } catch {
          setInitials(user.email?.charAt(0).toUpperCase() || '?');
        }
      }
    });
    return () => unsub();
  }, [auth]);

  if (!authReady) {
    return (
      <div className="app-launch" aria-busy="true" aria-label="Loading FalconRide">
        <img src={process.env.PUBLIC_URL + '/mascot-logo.png'} alt="" className="app-launch-logo" />
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        {isLoggedIn && <TopNav initials={initials} />}
        {isLoggedIn && !isVerified && <VerifyEmailBanner />}
        {isLoggedIn && needsTerms && <TermsAgreement />}

        <Routes>
          <Route path="/"       element={isLoggedIn ? <Navigate replace to="/home" /> : <LoginPage setLoggedIn={setIsLoggedIn} />} />
          <Route path="/signup" element={isLoggedIn ? <Navigate replace to="/home" /> : <SignUp    setLoggedIn={setIsLoggedIn} />} />
          {isLoggedIn ? (
            <>
              <Route path="/home"       element={<Home />} />
              <Route path="/rides"      element={<Rides />} />
              <Route path="/reserve"    element={<Navigate replace to="/rides" />} />
              <Route path="/request"    element={<Navigate replace to="/rides" />} />
              <Route path="/borrow"     element={<Borrow />} />
              <Route path="/activity"   element={<Activity />} />
              <Route path="/profile"    element={<Profile />} />
              <Route path="/settings"   element={<Settings />} />
              <Route path="/admin"      element={<Admin />} />
              <Route path="/addreserve" element={<PostForm key="ride" kind="ride" />} />
              <Route path="/addrequest" element={<PostForm key="request" kind="request" />} />
            </>
          ) : (
            <Route path="*" element={<Navigate replace to="/" />} />
          )}
        </Routes>

        {isLoggedIn && (
          <nav className="bottom-tab-bar">
            <NavLink to="/home"     className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon">{IC.home}</span>
              <span className="nav-label">Home</span>
            </NavLink>
            <NavLink to="/rides"    className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon">{IC.book}</span>
              <span className="nav-label">Rides</span>
            </NavLink>
            <NavLink to="/borrow"   className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon">{IC.borrow}</span>
              <span className="nav-label">Borrow</span>
            </NavLink>
            <NavLink to="/activity" className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon">{IC.activity}</span>
              <span className="nav-label">Activity</span>
            </NavLink>
            <NavLink to="/profile"  className={({ isActive }) => isActive ? 'active' : ''}>
              <span className="nav-icon">{IC.profile}</span>
              <span className="nav-label">Profile</span>
            </NavLink>
          </nav>
        )}
      </div>
    </Router>
  );
}

export default App;
